import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, ArrowLeft, Save, Loader2, Power, Clock, Calendar, MessageSquare, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminHiringSettings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    hiring_enabled: false,
    countdown_message: 'Hiring opens soon! Browse available tradespeople while you wait.',
    countdown_date: ''
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
      fetchSettings();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hiring/admin/settings`, {
        withCredentials: true
      });
      if (response.data) {
        setSettings({
          hiring_enabled: response.data.hiring_enabled || false,
          countdown_message: response.data.countdown_message || '',
          countdown_date: response.data.countdown_date || ''
        });
      }
    } catch (error) {
      // Using default hiring settings - expected for new setups
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/hiring/admin/settings`, settings, {
        withCredentials: true
      });
      toast.success(settings.hiring_enabled ? 'Hiring is now LIVE!' : 'Hiring settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleHiring = async () => {
    const newValue = !settings.hiring_enabled;
    setSettings(prev => ({ ...prev, hiring_enabled: newValue }));
    
    // Auto-save when toggling
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/hiring/admin/settings`, {
        ...settings,
        hiring_enabled: newValue
      }, {
        withCredentials: true
      });
      toast.success(newValue ? 'Hiring is now LIVE! Employers can contact tradespeople.' : 'Hiring is now DISABLED. Employers can only browse.');
    } catch (error) {
      toast.error('Failed to update hiring status');
      setSettings(prev => ({ ...prev, hiring_enabled: !newValue })); // Revert
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>Back to Dashboard</span>
              </button>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">Welcome, {user?.name}</span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Hiring Settings</h1>
          <p className="text-slate-600 mt-2">Control when employers can contact and hire tradespeople</p>
        </div>

        {/* Main Toggle Card */}
        <div className={`rounded-xl border-2 p-6 mb-6 transition-colors ${
          settings.hiring_enabled 
            ? 'bg-green-50 border-green-300' 
            : 'bg-amber-50 border-amber-300'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                settings.hiring_enabled ? 'bg-green-500' : 'bg-amber-500'
              }`}>
                <Power className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">
                  {settings.hiring_enabled ? 'Hiring is LIVE' : 'Hiring is DISABLED'}
                </h2>
                <p className="text-slate-600 mt-1">
                  {settings.hiring_enabled 
                    ? 'Employers can browse, unlock profiles, and contact tradespeople'
                    : 'Employers can browse tradespeople but cannot contact or unlock profiles'
                  }
                </p>
              </div>
            </div>
            <button
              onClick={handleToggleHiring}
              disabled={saving}
              className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                settings.hiring_enabled
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-green-500 hover:bg-green-600'
              } disabled:opacity-50`}
            >
              {saving ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : settings.hiring_enabled ? (
                'Disable Hiring'
              ) : (
                'Enable Hiring'
              )}
            </button>
          </div>
        </div>

        {/* Countdown Settings (only shown when hiring is disabled) */}
        {!settings.hiring_enabled && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Clock className="h-6 w-6 text-orange-500" />
              <h3 className="text-lg font-semibold text-slate-900">Countdown Settings</h3>
            </div>
            <p className="text-slate-600 text-sm mb-6">
              Configure the message and date shown to employers while hiring is disabled.
            </p>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <MessageSquare className="h-4 w-4 inline mr-2" />
                  Countdown Message
                </label>
                <textarea
                  value={settings.countdown_message}
                  onChange={(e) => setSettings(prev => ({ ...prev, countdown_message: e.target.value }))}
                  rows={3}
                  placeholder="E.g., Hiring opens January 15th! Browse our skilled tradespeople and get ready to find the perfect match."
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  This message appears to employers when they try to contact or unlock a tradesperson's profile.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Countdown Date (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={settings.countdown_date}
                  onChange={(e) => setSettings(prev => ({ ...prev, countdown_date: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  If set, employers will see a countdown timer to this date. Leave empty to show only the message.
                </p>
              </div>

              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 font-medium"
              >
                {saving ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    Save Countdown Settings
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Preview Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-slate-500" />
            <h3 className="text-lg font-semibold text-slate-900">Employer Preview</h3>
          </div>
          <p className="text-slate-600 text-sm mb-4">
            This is what employers will see when hiring is disabled:
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-amber-900">Hiring Opens Soon</h4>
                <p className="text-amber-800 mt-1">
                  {settings.countdown_message || 'Hiring opens soon! Browse available tradespeople while you wait.'}
                </p>
                {settings.countdown_date && (
                  <div className="mt-3 flex items-center gap-2 text-amber-700">
                    <Calendar className="h-4 w-4" />
                    <span className="font-medium">
                      Opens: {new Date(settings.countdown_date).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* What's Blocked Info */}
        <div className="mt-6 bg-slate-100 rounded-lg p-4">
          <h4 className="font-medium text-slate-900 mb-2">When hiring is disabled, employers cannot:</h4>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>• Unlock or view full tradesperson profiles</li>
            <li>• Reveal contact information</li>
            <li>• Start hiring conversations</li>
            <li>• Contact On-Call tradespeople</li>
            <li>• Send urgent job requests</li>
          </ul>
          <p className="text-sm text-slate-500 mt-3 italic">
            Employers can still browse, search, and view basic profile information.
          </p>
        </div>
      </main>
    </div>
  );
}
